import {
  AssignmentDetailsActions,
  AssignmentDetailsMutations,
  AssignmentDetailsGetters
} from './assignment-details.keys';
import { assignmentDetailsService } from './assignment-details.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { TolMessagesActions } from '@/app/features/tol-messages/tol-messages.keys';
import i18n from '@/i18n';
import { pick } from 'ramda';
import router from '@/router';

export const getEditableAssignmentFields = () => {
  return [
    'stakeholderClientContacts',
    'stakeholderCommissions',
    'rates',
    'purchaseOrderDetails',
    'reference_number',
    'created_by_id',
    'created_comment',
    'status',
    'start_date',
    'stop_date',
    'branch_name',
    'division_abbr',
    'department_code',
    'contractor_id',
    'client_id',
    'end_client_id',
    'billing_method_id',
    'work_order_number',
    'client_number',
    'payroll_number',
    'purchase_order_effective_date',
    'pay_period_type',
    'submittal_period_type',
    'submittal_period_effective_date',
    'week_end_day',
    'time_sheet_submittal',
    'expense_submittal',
    'deposit_slip_access',
    'occupation',
    'work_location_province_abbr',
    'work_location_address_id',
    'work_authorization',
    'purchase_order_id',
    'is_clerical',
    'burden',
    'isPayrollee',
    'payrollee_code',
    'is_referral',
    'rebate_id',
    'taxable_expense',
    'general_comments',
    'pay_comments',
    'bill_comments',
    'external_notes',
    'assets',
    'asset_numbers',
    'schedule_f',
    'hiring_manager',
    'reason_id',
    'oldEndDate',
    'payrollnum_id',
    'ibm_extra_info',
    'ibmExtraInfo',
    'payroll_number'
  ];
};

export const getDefaultState = () => {
  return {
    isDetailsLoading: false,
    isOnScreenAssignmentModified: false,
    isLoadingAttachments: false,
    isAssignmentTimesheetsLoading: false,
    isAssignmentExpensesLoading: false,
    currentAssignment: {},
    currentAssignmentTimeSheets: [],
    currentAssignmentExpenses: [],
    currentAssignmentAttachments: [],
    currentAssignmentHistories: [],
    assignmentValidationErrors: {},
    assignmentTimesSheetSortOrder: {
      sortBy: 'startDate',
      sortDirection: 'desc'
    },
    assignmentExpensesSortOrder: {
      sortBy: 'startDate',
      sortDirection: 'desc'
    },
    assignmentTimeSheetPagination: {
      recordsPerPage: 10,
      offset: 0
    },
    assignmentExpensePagination: {
      recordsPerPage: 10,
      offset: 0
    },
    ibmExtraInfo: {},
    assignmentTimeSheetRecordCount: 0,

    assignmentExpenseRecordCount: 0
  };
};

export const assignmentDetailsStore = {
  state: getDefaultState(),
  getters: {
    [AssignmentDetailsGetters.AssignmentTimeSheetsPerPage]: state => {
      return state.assignmentTimeSheetPagination.recordsPerPage;
    },
    [AssignmentDetailsGetters.AssignmentExpensesPerPage]: state => {
      return state.assignmentExpensePagination.recordsPerPage;
    },
    [AssignmentDetailsGetters.CurrentAssignmentTimeSheetsPage]: state => {
      return state.assignmentTimeSheetPagination.offset;
    },
    [AssignmentDetailsGetters.CurrentAssignmentExpensesPage]: state => {
      return state.assignmentExpensePagination.offset;
    },
    [AssignmentDetailsGetters.AssignmentTimeSheetCount]: state =>
      state.assignmentTimeSheetRecordCount,
    [AssignmentDetailsGetters.AssignmentExpenseCount]: state => state.assignmentExpenseRecordCount,
    [AssignmentDetailsGetters.IsAssignmentDetailsLoading]: state => {
      return state.isDetailsLoading;
    },
    [AssignmentDetailsGetters.IsAssignmentTimesheetsLoading]: state => {
      return state.isAssignmentTimesheetsLoading;
    },
    [AssignmentDetailsGetters.IsAssignmentExpensesLoading]: state => {
      return state.isAssignmentExpensesLoading;
    },
    [AssignmentDetailsGetters.CurrentAssignment]: state => {
      return state.currentAssignment;
    },
    [AssignmentDetailsGetters.CurrentAssignmentTimeSheets]: state => {
      return state.currentAssignmentTimeSheets;
    },
    [AssignmentDetailsGetters.CurrentAssignmentExpenses]: state => {
      return state.currentAssignmentExpenses;
    },
    [AssignmentDetailsGetters.CurrentAssignmentHistories]: state => {
      return state.currentAssignmentHistories;
    },
    [AssignmentDetailsGetters.IsOnScreenAssignmentModified]: state => {
      return state.isOnScreenAssignmentModified;
    },
    [AssignmentDetailsGetters.AssignmentId]: state => {
      return state.currentAssignment.id;
    },
    [AssignmentDetailsGetters.AssignmentValidationErrors]: state => {
      return state.assignmentValidationErrors;
    },
    [AssignmentDetailsGetters.SelectedHistoryRecord]: state => assignmentId => {
      return state.currentAssignmentHistories.filter(
        historyRecord => historyRecord.id === assignmentId
      )[0];
    },
    [AssignmentDetailsGetters.AssignmentTimeSheetsSortOrder]: state => {
      return state.assignmentTimesSheetSortOrder;
    },
    [AssignmentDetailsGetters.AssignmentExpensesSortOrder]: state => {
      return state.assignmentExpensesSortOrder;
    },
    [AssignmentDetailsGetters.CurrentAssignmentAttachments]: state =>
      state.currentAssignmentAttachments
  },
  mutations: {
    [AssignmentDetailsMutations.SetAssignmentDetailsLoading]: (state, isLoading = true) => {
      state.isDetailsLoading = isLoading;
    },
    [AssignmentDetailsMutations.SetAssignmentTimesheetsLoading]: (state, isLoading = true) => {
      state.isAssignmentTimesheetsLoading = isLoading;
    },
    [AssignmentDetailsMutations.SetAssignmentExpensesLoading]: (state, isLoading = true) => {
      state.isAssignmentExpensesLoading = isLoading;
    },
    [AssignmentDetailsMutations.RetrieveAssignmentSuccess]: (state, assignment) => {
      assignment.purchase_order_effective_date = null;
      state.currentAssignment = assignment;
      state.isOnScreenAssignmentModified = false;
    },
    [AssignmentDetailsMutations.RetrieveAssignmentTimeSheetsSuccess]: (state, timeSheets) => {
      state.currentAssignmentTimeSheets = timeSheets;
    },
    [AssignmentDetailsMutations.RetrieveAssignmentExpensesSuccess]: (state, expenses) => {
      state.currentAssignmentExpenses = expenses;
    },
    [AssignmentDetailsMutations.RetrieveAssignmentAttachmentsSuccess]: (state, attachments) => {
      state.currentAssignmentAttachments = attachments;
    },
    [AssignmentDetailsMutations.RetrieveAssignmentHistoriesSuccess]: (state, histories) => {
      state.currentAssignmentHistories = histories;
    },
    [AssignmentDetailsMutations.SetAssignmentValidationErrorsSuccess]: (state, errors) => {
      state.assignmentValidationErrors = errors;
    },
    [AssignmentDetailsMutations.DeleteAssignmentValidationErrorKeySuccess]: (state, key) => {
      const updatedErrors = Object.assign({}, state.assignmentValidationErrors);
      delete updatedErrors[key];
      state.assignmentValidationErrors = updatedErrors;
    },
    [AssignmentDetailsMutations.SetAssignmentTimeSheetsSortOrder]: (state, sortOrder) => {
      state.assignmentTimesSheetSortOrder = sortOrder;
    },
    [AssignmentDetailsMutations.SetAssignmentExpensesSortOrder]: (state, sortOrder) => {
      state.assignmentExpensesSortOrder = sortOrder;
    },
    [AssignmentDetailsMutations.SetAssignmentTimeSheetsPaginationParams](state, pagination) {
      state.assignmentTimeSheetPagination = pagination;
    },
    [AssignmentDetailsMutations.SetAssignmentExpensesPaginationParams](state, pagination) {
      state.assignmentExpensePagination = pagination;
    },
    [AssignmentDetailsMutations.ResetAssignmentTimeSheetPaginationOffset](state) {
      state.assignmentTimeSheetPagination.offset = 0;
    },
    [AssignmentDetailsMutations.ResetAssignmentExpensePaginationOffset](state) {
      state.assignmentExpensePagination.offset = 0;
    },
    [AssignmentDetailsMutations.SetAssignmentTimeSheetRecordCount](state, recordCount) {
      state.assignmentTimeSheetRecordCount = recordCount;
    },
    [AssignmentDetailsMutations.SetAssignmentExpenseRecordCount](state, recordCount) {
      state.assignmentExpenseRecordCount = recordCount;
    }
  },
  actions: {
    [AssignmentDetailsActions.RetrieveAssignment]: ({ commit, dispatch }, referenceNumber) => {
      commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading);
      return assignmentDetailsService
        .retrieveAssignment(referenceNumber)
        .then(assignment => {
          commit(AssignmentDetailsMutations.RetrieveAssignmentSuccess, assignment);
        })
        .then(() => {
          commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignment.details.errors.retrieveAssignment')
          });
          commit(AssignmentDetailsMutations.SetLoading, false);
        });
    },
    [AssignmentDetailsActions.DoAssignmentAction]: ({ dispatch, commit }, params) => {
      let { assignment, action } = params;
      assignment = pick(getEditableAssignmentFields(), assignment);
      commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading);
      assignmentDetailsService
        .doAssignmentAction(assignment, action)
        .then(status => {
          if (status.message === 'success') {
            dispatch(AssignmentDetailsActions.RetrieveAssignment, status.data);
            dispatch(AssignmentDetailsActions.SetAssignmentValidationErrors);
            if (Object.keys(status.info).length > 0) {
              dispatch(TolMessagesActions.SetMessages, status.info);
            }
            router.replace({ path: `/assignments/${status.data}` }).catch(() => {});
          } else {
            dispatch(AssignmentDetailsActions.SetAssignmentValidationErrors, status.errors);
            const messages = Object.values(status.errors);
            let items = [];
            for (let item of messages) {
              items = items.concat(item);
            }
            dispatch(TolErrorsActions.SetError, { items });
          }
        })
        .then(() => {
          commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, { error });
        });
    },
    [AssignmentDetailsActions.SetAssignmentValidationErrors]: ({ commit }, errors = {}) => {
      commit(AssignmentDetailsMutations.SetAssignmentValidationErrorsSuccess, errors);
    },
    [AssignmentDetailsActions.DeleteAssignmentValidationErrorKey]: ({ commit }, errorKey) => {
      commit(AssignmentDetailsMutations.DeleteAssignmentValidationErrorKeySuccess, errorKey);
    },
    [AssignmentDetailsActions.SetAssignmentTimeSheetsSortOrder]: (
      { state, commit, dispatch },
      sortOrder
    ) => {
      commit(AssignmentDetailsMutations.SetAssignmentTimeSheetsSortOrder, sortOrder);
      dispatch(
        //eslint-disable-next-line: variable-name
        AssignmentDetailsActions.RetrieveAssignmentTimeSheets,
        state.currentAssignment.reference_number
      );
    },
    [AssignmentDetailsActions.SetAssignmentExpensesSortOrder]: (
      { state, commit, dispatch },
      sortOrder
    ) => {
      commit(AssignmentDetailsMutations.SetAssignmentExpensesSortOrder, sortOrder);
      dispatch(
        //eslint-disable-next-line: variable-name
        AssignmentDetailsActions.RetrieveAssignmentExpenses,
        state.currentAssignment.reference_number
      );
    },

    [AssignmentDetailsActions.RetrieveAssignmentTimeSheets]: (
      { state, commit, dispatch },
      referenceNumber
    ) => {
      commit(AssignmentDetailsMutations.SetAssignmentTimesheetsLoading);

      dispatch(AssignmentDetailsActions.RetrieveAssignmentTimeSheetsRecordCount, referenceNumber);

      return assignmentDetailsService
        .retrieveAssignmentTimeSheets(
          referenceNumber,
          state.assignmentTimesSheetSortOrder,
          state.assignmentTimeSheetPagination
        )
        .then(timeSheets => {
          commit(AssignmentDetailsMutations.RetrieveAssignmentTimeSheetsSuccess, timeSheets);
          commit(AssignmentDetailsMutations.SetAssignmentTimesheetsLoading, false);
        });
    },

    [AssignmentDetailsActions.RetrieveAssignmentTimeSheetsRecordCount]: (
      { commit, dispatch },
      assignmentReferenceNumber
    ) => {
      return assignmentDetailsService
        .retrieveTimeSheetsRecordCount(assignmentReferenceNumber)
        .then(recordCount => {
          commit(AssignmentDetailsMutations.SetAssignmentTimeSheetRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignments.listing.errors.retrieveAssignments')
          });
          commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading, false);
        });
    },

    [AssignmentDetailsActions.SetAssignmentTimeSheetsPaginationParams](
      { commit, dispatch },
      options
    ) {
      commit(AssignmentDetailsMutations.SetAssignmentTimeSheetsPaginationParams, options.event);
      dispatch(AssignmentDetailsActions.RetrieveAssignmentTimeSheets, options.referenceNumber);
    },

    [AssignmentDetailsActions.RetrieveAssignmentExpenses]: (
      { state, commit, dispatch },
      referenceNumber
    ) => {
      commit(AssignmentDetailsMutations.SetAssignmentExpensesLoading);
      dispatch(AssignmentDetailsActions.RetrieveAssignmentExpensesRecordCount, referenceNumber);
      return assignmentDetailsService
        .retrieveAssignmentExpenses(
          referenceNumber,
          state.assignmentExpensesSortOrder,
          state.assignmentExpensePagination
        )
        .then(expenses => {
          commit(AssignmentDetailsMutations.RetrieveAssignmentExpensesSuccess, expenses);
          commit(AssignmentDetailsMutations.SetAssignmentExpensesLoading, false);
        });
    },
    [AssignmentDetailsActions.RetrieveAssignmentExpensesRecordCount]: (
      { commit, dispatch },
      assignmentReferenceNumber
    ) => {
      return assignmentDetailsService
        .retrieveExpensesRecordCount(assignmentReferenceNumber)
        .then(recordCount => {
          commit(AssignmentDetailsMutations.SetAssignmentExpenseRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignments.listing.errors.retrieveAssignments')
          });
          commit(AssignmentDetailsMutations.SetAssignmentDetailsLoading, false);
        });
    },

    [AssignmentDetailsActions.SetAssignmentExpensesPaginationParams](
      { commit, dispatch },
      options
    ) {
      commit(AssignmentDetailsMutations.SetAssignmentExpensesPaginationParams, options.event);
      dispatch(AssignmentDetailsActions.RetrieveAssignmentExpenses, options.referenceNumber);
    },

    [AssignmentDetailsActions.RetrieveAssignmentHistories]: (
      { state, commit },
      referenceNumber
    ) => {
      return assignmentDetailsService
        .retrieveAssignmentHistories(referenceNumber, state.assignmentHistoriesSortOrder)
        .then(histories => {
          commit(AssignmentDetailsMutations.RetrieveAssignmentHistoriesSuccess, histories);
        });
    },
    [AssignmentDetailsActions.RetrieveAssignmentAttachments]: (
      { state, commit },
      referenceNumber
    ) => {
      return assignmentDetailsService
        .retrieveAssignmentAttachments(referenceNumber, state.assignmentAttachmentsSortOrder)
        .then(attachments => {
          commit(AssignmentDetailsMutations.RetrieveAssignmentAttachmentsSuccess, attachments);
        });
    },
    [AssignmentDetailsActions.CreateNewTimesheet]: ({ dispatch }, timesheetFields) => {
      return assignmentDetailsService.createTimesheet(timesheetFields).then(status => {
        if (status.message === 'success') {
          dispatch(AssignmentDetailsActions.RetrieveAssignmentTimeSheets, status.assignementRef);

          if (Object.keys(status.info).length > 0) {
            dispatch(TolMessagesActions.SetMessages, status.info);
          }

          router.replace({ path: `/assignments/${status.assignementRef}` }).catch(() => {});
        } else {
          dispatch(TolMessagesActions.ClearAll);
          const messages = Object.values(status.errors);
          let items = [];
          for (let item of messages) {
            items = items.concat(item);
          }
          dispatch(TolErrorsActions.SetError, { items });
        }
      });
    },
    [AssignmentDetailsActions.RetrieveIfNotLoaded]: ({ dispatch }, params) => {
      switch (params.item) {
        case 'TIMESHEETS':
          dispatch(AssignmentDetailsActions.RetrieveAssignmentTimeSheets, params.referenceNumber);
          break;
        case 'ATTACHMENTS':
          dispatch(AssignmentDetailsActions.RetrieveAssignmentAttachments, params.referenceNumber);
          break;
        case 'EXPENSES':
          dispatch(AssignmentDetailsActions.RetrieveAssignmentExpenses, params.referenceNumber);
          break;
        case 'HISTORY':
          dispatch(AssignmentDetailsActions.RetrieveAssignmentHistories, params.referenceNumber);
          break;
      }
    }
  }
};
